import axios from 'axios';
import { io, Socket } from 'socket.io-client';

// Define base URL for WhatsApp Web service
const WHATSAPP_WEB_BASE_URL = process.env.REACT_APP_WHATSAPP_WEB_URL || 'http://localhost:8006';

// Define types
export interface WhatsAppWebSession {
  sessionKey: string;
  status: 'initializing' | 'qr_received' | 'authenticated' | 'ready' | 'disconnected' | 'auth_failure';
  hasQR: boolean;
}

export interface WhatsAppWebMessage {
  id: string;
  from: string;
  to: string;
  body: string;
  timestamp: number;
  hasMedia: boolean;
  type: string;
  isGroup: boolean;
  author?: string;
}

export interface SendMessageParams {
  to: string;
  body: string;
}

export interface SendMessageResponse {
  success: boolean;
  messageId: string;
  timestamp: number;
}

// WhatsApp Web API client
class WhatsAppWebService {
  private socket: Socket | null = null;
  private sessionListeners: Map<string, ((data: any) => void)[]> = new Map();

  // Initialize socket connection
  public initSocket(): Socket {
    if (!this.socket) {
      this.socket = io(WHATSAPP_WEB_BASE_URL);
      
      this.socket.on('connect', () => {
        console.log('Connected to WhatsApp Web service');
      });
      
      this.socket.on('disconnect', () => {
        console.log('Disconnected from WhatsApp Web service');
      });
      
      // Set up event listeners for all registered sessions
      this.sessionListeners.forEach((listeners, sessionKey) => {
        this.setupSessionListeners(sessionKey);
      });
    }
    
    return this.socket;
  }
  
  // Create a new WhatsApp Web session
  public async createSession(userId: number, sessionId: string): Promise<WhatsAppWebSession> {
    try {
      const response = await axios.post(`${WHATSAPP_WEB_BASE_URL}/api/sessions`, {
        userId,
        sessionId
      });
      
      const sessionKey = response.data.sessionKey;
      this.setupSessionListeners(sessionKey);
      
      return {
        sessionKey,
        status: response.data.status,
        hasQR: false
      };
    } catch (error) {
      console.error('Error creating WhatsApp Web session:', error);
      throw error;
    }
  }
  
  // Get session status
  public async getSessionStatus(sessionKey: string): Promise<WhatsAppWebSession> {
    try {
      const response = await axios.get(`${WHATSAPP_WEB_BASE_URL}/api/sessions/${sessionKey}`);
      return response.data;
    } catch (error) {
      console.error('Error getting session status:', error);
      throw error;
    }
  }
  
  // Get QR code for session
  public async getSessionQR(sessionKey: string): Promise<string> {
    try {
      const response = await axios.get(`${WHATSAPP_WEB_BASE_URL}/api/sessions/${sessionKey}/qr`);
      return response.data.qr;
    } catch (error) {
      console.error('Error getting session QR code:', error);
      throw error;
    }
  }
  
  // Send message
  public async sendMessage(sessionKey: string, params: SendMessageParams): Promise<SendMessageResponse> {
    try {
      const response = await axios.post(
        `${WHATSAPP_WEB_BASE_URL}/api/sessions/${sessionKey}/messages`,
        params
      );
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }
  
  // Delete session
  public async deleteSession(sessionKey: string): Promise<void> {
    try {
      await axios.delete(`${WHATSAPP_WEB_BASE_URL}/api/sessions/${sessionKey}`);
      
      // Remove session listeners
      if (this.socket) {
        this.socket.off(`session:${sessionKey}:qr`);
        this.socket.off(`session:${sessionKey}:status`);
        this.socket.off(`session:${sessionKey}:message`);
      }
      
      this.sessionListeners.delete(sessionKey);
    } catch (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  }
  
  // Add event listener for session events
  public addSessionListener(
    sessionKey: string,
    eventType: 'qr' | 'status' | 'message',
    listener: (data: any) => void
  ): void {
    // Initialize listeners array for this session if it doesn't exist
    if (!this.sessionListeners.has(sessionKey)) {
      this.sessionListeners.set(sessionKey, []);
    }
    
    // Add listener to array
    const listeners = this.sessionListeners.get(sessionKey)!;
    listeners.push(listener);
    
    // Set up socket listeners if socket is connected
    if (this.socket && this.socket.connected) {
      this.setupSessionListeners(sessionKey);
    }
  }
  
  // Remove event listener for session events
  public removeSessionListener(
    sessionKey: string,
    eventType: 'qr' | 'status' | 'message',
    listener: (data: any) => void
  ): void {
    if (!this.sessionListeners.has(sessionKey)) {
      return;
    }
    
    const listeners = this.sessionListeners.get(sessionKey)!;
    const index = listeners.indexOf(listener);
    
    if (index !== -1) {
      listeners.splice(index, 1);
    }
    
    if (listeners.length === 0) {
      this.sessionListeners.delete(sessionKey);
      
      // Remove socket listeners
      if (this.socket) {
        this.socket.off(`session:${sessionKey}:qr`);
        this.socket.off(`session:${sessionKey}:status`);
        this.socket.off(`session:${sessionKey}:message`);
      }
    }
  }
  
  // Set up socket listeners for a session
  private setupSessionListeners(sessionKey: string): void {
    if (!this.socket) {
      return;
    }
    
    // Set up QR code listener
    this.socket.on(`session:${sessionKey}:qr`, (qr: string) => {
      const listeners = this.sessionListeners.get(sessionKey);
      if (listeners) {
        listeners.forEach(listener => listener({ type: 'qr', qr }));
      }
    });
    
    // Set up status listener
    this.socket.on(`session:${sessionKey}:status`, (data: { status: string, error?: string }) => {
      const listeners = this.sessionListeners.get(sessionKey);
      if (listeners) {
        listeners.forEach(listener => listener({ type: 'status', ...data }));
      }
    });
    
    // Set up message listener
    this.socket.on(`session:${sessionKey}:message`, (message: WhatsAppWebMessage) => {
      const listeners = this.sessionListeners.get(sessionKey);
      if (listeners) {
        listeners.forEach(listener => listener({ type: 'message', message }));
      }
    });
  }
}

// Export singleton instance
export const whatsappWebService = new WhatsAppWebService();
export default whatsappWebService;
